import { useEffect, useState } from 'react';
import type { Vendor } from '@shopgnd/shared';
import { getVendor, getVendorForOwnerUser } from '@/api/catalog';
import { canAdminImpersonate } from '@/lib/adminImpersonation';
import { subscribeVendorProfileRefresh } from '@/lib/vendorProfileRefresh';
import { useAdminImpersonationStore } from '@/stores/adminImpersonationStore';
import { useAuthStore } from '@/stores/authStore';

export function useVendorProfile(): { vendor: Vendor | null; ready: boolean } {
  const user = useAuthStore((s) => s.user);
  const impersonation = useAdminImpersonationStore((s) => s.session);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const impersonatingVendor =
    impersonation?.kind === 'vendor' && canAdminImpersonate(user);
  const [ready, setReady] = useState(() => !user?.id && !impersonatingVendor);

  useEffect(() => {
    if (impersonatingVendor && impersonation?.kind === 'vendor') {
      let cancelled = false;
      setReady(false);
      const load = () =>
        void getVendor(impersonation.vendorId)
          .then((v) => {
            if (cancelled) return;
            setVendor(v ?? null);
            setReady(true);
          })
          .catch(() => {
            if (cancelled) return;
            setVendor(null);
            setReady(true);
          });
      load();
      const unsub = subscribeVendorProfileRefresh(load);
      return () => {
        cancelled = true;
        unsub();
      };
    }

    if (!user?.id) {
      setVendor(null);
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);
    const load = async () => {
      try {
        const owned = await getVendorForOwnerUser(user.id);
        if (cancelled) return;
        if (owned) {
          setVendor(owned);
          setReady(true);
          return;
        }
        // Hard refresh can race Cognito session hydration — retry once before giving up.
        await new Promise((r) => setTimeout(r, 500));
        if (cancelled) return;
        const retry = await getVendorForOwnerUser(user.id);
        if (cancelled) return;
        setVendor(retry ?? null);
        setReady(true);
      } catch {
        if (cancelled) return;
        setVendor(null);
        setReady(true);
      }
    };
    void load();
    const unsub = subscribeVendorProfileRefresh(() => {
      if (cancelled) return;
      void load();
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [user, impersonation, impersonatingVendor]);

  return { vendor, ready };
}

export function useVendorForUser(): Vendor | null {
  return useVendorProfile().vendor;
}
